import { t, tf } from "./i18n.js";
import { erzeugeAufnahmeEingabe, istCheckoutDart } from "./aufnahme-eingabe.js";
// ==========================================
// RANDOM CHECKOUT LOGIK
//
// Es wird eine Zufallszahl aus einem konfigurierbaren Bereich gezogen, die
// innerhalb von X Darts gecheckt werden muss. Nach N Runden ist Schluss.
//
// Alles modular ueber randomConfig:
//   minTarget / maxTarget : Bereich der Zufallszahl
//   dartsPerAttempt       : wie viele Darts pro Versuch zur Verfuegung stehen
//   rounds                : Anzahl der Versuche
//   allowRepeat           : darf dieselbe Zahl mehrfach kommen?
// ==========================================

// Zahlen, die mit einem Doppel nicht gecheckt werden koennen.
const RANDOM_BOGEY = [159, 162, 163, 165, 166, 168, 169];
const RANDOM_MAX_CHECKOUT = 170;

let randomConfig = {
  minTarget: 41,
  maxTarget: 170,
  dartsPerAttempt: 3,
  rounds: 10,
  allowRepeat: false,
};

let randomState = null;
let randomHistoryStack = [];

// ------------------------------------------------------------------
// HILFSFUNKTIONEN
// ------------------------------------------------------------------

// Alle in diesem Bereich tatsaechlich checkbaren Zahlen.
function randomBuildPool(min, max) {
  const pool = [];
  for (let n = Math.max(2, min); n <= Math.min(max, RANDOM_MAX_CHECKOUT); n++) {
    if (RANDOM_BOGEY.includes(n)) continue;
    pool.push(n);
  }
  return pool;
}

function randomPickTarget() {
  let pool = randomState.pool;

  if (!randomConfig.allowRepeat) {
    const used = randomState.attempts.map((a) => a.target);
    const unused = pool.filter((n) => !used.includes(n));
    // Sind alle Zahlen durch, faengt der Pool wieder von vorne an.
    if (unused.length > 0) pool = unused;
  }

  return pool[Math.floor(Math.random() * pool.length)];
}

// ------------------------------------------------------------------
// SETUP
// ------------------------------------------------------------------

function openRandomSetup() {
  openGameSetup("random-setup-screen", "RANDOM CHECKOUT", "random-player-input", "random");
  applyRandomPreset("medium", false);
}

// Voreinstellungen, die die Felder befuellen. Manuell weiterhin ueberschreibbar.
const RANDOM_PRESETS = {
  easy: { minTarget: 2, maxTarget: 60, dartsPerAttempt: 6, rounds: 10 },
  medium: { minTarget: 41, maxTarget: 110, dartsPerAttempt: 6, rounds: 10 },
  hard: { minTarget: 101, maxTarget: 170, dartsPerAttempt: 9, rounds: 10 },
};

function applyRandomPreset(key, notify = true) {
  const preset = RANDOM_PRESETS[key];
  if (!preset) return;
  setValue("random-min", preset.minTarget);
  setValue("random-max", preset.maxTarget);
  setValue("random-darts", preset.dartsPerAttempt);
  setValue("random-rounds", preset.rounds);
  if (notify && typeof showToast === "function") {
    showToast(tf("js.random.005", { p1: key }), "success");
  }
}

function setValue(id, value) {
  const el = document.getElementById(id);
  if (el) el.value = value;
}

function readIntField(id, fallback) {
  const el = document.getElementById(id);
  const parsed = parseInt(el ? el.value : "");
  return Number.isFinite(parsed) ? parsed : fallback;
}

function startRandomGame() {
  const min = readIntField("random-min", 41);
  const max = readIntField("random-max", 170);
  const darts = readIntField("random-darts", 6);
  const rounds = readIntField("random-rounds", 10);

  if (min > max) {
    showToast(t("js.random.001"), "error");
    return;
  }
  if (darts < 1 || rounds < 1) {
    showToast(t("js.random.002"), "error");
    return;
  }

  const pool = randomBuildPool(min, max);
  if (pool.length === 0) {
    showToast(t("js.random.003"), "error");
    return;
  }

  randomConfig = {
    minTarget: min,
    maxTarget: max,
    dartsPerAttempt: darts,
    rounds: rounds,
    allowRepeat: !!document.getElementById("random-allow-repeat")?.checked,
  };

  randomState = {
    playerName:
      document.getElementById("random-player-input")?.value.trim() || t("js.app.009"),
    pool: pool,
    attempts: [],
    currentRound: 1,
    target: null,
    hits: 0,
    dartsUsedTotal: 0,
    isOver: false,
    // NEU (14.08.2026): der laufende Versuch. `rest` ist das, was von der
    // Zielzahl noch steht, `dartsUsedAttempt` wie viele der erlaubten Darts
    // dafuer schon weg sind. Beides steckt im randomState, damit der
    // Undo-Schnappschuss (JSON-Kopie) sie ohne Zutun mitnimmt.
    rest: 0,
    dartsUsedAttempt: 0,
  };
  randomNeuesZiel();

  randomHistoryStack = [];

  const undoBtn = document.getElementById("btn-undo-random");
  if (undoBtn) undoBtn.style.display = "none";
  document.getElementById("random-setup-screen").style.display = "none";
  document.getElementById("game-random-screen").style.display = "block";

  // ERST sichtbar, DANN bauen - createDartboard misst beim Zeichnen.
  randomEingabe.baue();
  randomEingabe.leere();
  randomEingabe.aktualisiereSichtbarkeit();

  updateRandomUI();

  // Spielwache scharf (29.08.2026).
  spielwacheSpielGestartet("random", sichereRandomStand);
}

// ── Spielwache: sichern & wiederherstellen (29.08.2026) ─────────────────
// randomState traegt den laufenden Versuch (rest, dartsUsedAttempt) selbst -
// derselbe Grund, aus dem der Undo-Schnappschuss so einfach ist.
function sichereRandomStand() {
  return { config: randomConfig, state: randomState };
}

function stelleRandomWieder(stand) {
  openRandomSetup();
  randomConfig = { ...stand.config };
  randomState = JSON.parse(JSON.stringify(stand.state));
  randomHistoryStack = [];

  // Setup-Formular angleichen, damit Revanche/Setup danach stimmen.
  const felder = {
    "random-min": randomConfig.minTarget,
    "random-max": randomConfig.maxTarget,
    "random-darts": randomConfig.dartsPerAttempt,
    "random-rounds": randomConfig.rounds,
  };
  for (const [id, wert] of Object.entries(felder)) {
    const feld = document.getElementById(id);
    if (feld) feld.value = wert;
  }
  const wiederholung = document.getElementById("random-allow-repeat");
  if (wiederholung) wiederholung.checked = !!randomConfig.allowRepeat;
  const nameFeld = document.getElementById("random-player-input");
  if (nameFeld) nameFeld.value = randomState.playerName;
  const undoBtn = document.getElementById("btn-undo-random");
  if (undoBtn) undoBtn.style.display = "none";

  document.getElementById("random-setup-screen").style.display = "none";
  document.getElementById("game-random-screen").style.display = "block";
  randomEingabe.baue();
  randomEingabe.leere();
  randomEingabe.aktualisiereSichtbarkeit();
  updateRandomUI();
  spielwacheSpielGestartet("random", sichereRandomStand);
}

spielwacheRegistriereFortsetzen("random", "Random Checkout", stelleRandomWieder);

/** Neue Zufallszahl ziehen und den Versuch zuruecksetzen.
 *
 *  Eine Stelle statt drei: Vorher stand `target = randomPickTarget()` in
 *  startRandomGame, submitRandomAttempt und skipRandomTarget. Mit `rest` und
 *  `dartsUsedAttempt` haetten drei Stellen dasselbe vergessen koennen. */
function randomNeuesZiel() {
  randomState.target = randomPickTarget();
  randomState.rest = randomState.target;
  randomState.dartsUsedAttempt = 0;
  if (randomEingabe) randomEingabe.leere();
}

function cancelRandomGame() {
  cancelCurrentGame("game-random-screen");
}

function resetRandomState() {
  randomState = null;
  randomHistoryStack = [];
  // Sonst haengen beim naechsten Spielstart noch die Darts des abgebrochenen
  // Versuchs in den Kacheln.
  if (randomEingabe) randomEingabe.leere();
}

// ------------------------------------------------------------------
// SPIELLOGIK
// ------------------------------------------------------------------

/** Schnappschuss fuer Undo - IMMER vor der ersten Aenderung.
 *
 *  Er stand vorher mitten in submitRandomAttempt. Mit der Dart-fuer-Dart-
 *  Eingabe faengt die Aenderung aber frueher an (der Rest sinkt schon
 *  waehrend der Aufnahme), deshalb ist das Sichern jetzt ein eigener
 *  Schritt, den der Aufrufer setzt. */
function randomSichern() {
  if (!randomState) return;
  randomHistoryStack.push({
    state: JSON.parse(JSON.stringify(randomState)),
  });
  const undoBtn = document.getElementById("btn-undo-random");
  if (undoBtn) undoBtn.style.display = "block";
}

// dartsUsed = 0 bedeutet "nicht geschafft".
// Oeffentlicher Weg (Tests, alte Aufrufer): sichert selbst.
function submitRandomAttempt(dartsUsed) {
  if (!randomState || randomState.isOver) return;
  if (dartsUsed < 0 || dartsUsed > randomConfig.dartsPerAttempt) return;
  randomSichern();
  randomWerteVersuch(dartsUsed);
}

/** Den Versuch abschliessen. Erwartet, dass bereits gesichert wurde. */
function randomWerteVersuch(dartsUsed) {
  const success = dartsUsed > 0;
  randomState.attempts.push({
    round: randomState.currentRound,
    target: randomState.target,
    darts: success ? dartsUsed : randomConfig.dartsPerAttempt,
    success: success,
  });

  if (success) {
    randomState.hits += 1;
    randomState.dartsUsedTotal += dartsUsed;
  } else {
    // BEWUSST unveraendert: Ein misslungener Versuch kostet weiterhin das
    // volle Dartbudget, auch wenn ein Bust ihn frueher beendet hat. Die
    // tatsaechliche Zahl waere ehrlicher, wuerde aber den Schnitt gegen alle
    // frueher gespeicherten Partien verschieben.
    randomState.dartsUsedTotal += randomConfig.dartsPerAttempt;
  }

  if (randomState.currentRound >= randomConfig.rounds) {
    randomState.isOver = true;
    updateRandomUI();
    finishRandomGame();
    return;
  }

  randomState.currentRound += 1;
  randomNeuesZiel();
  updateRandomUI();
}

function skipRandomTarget() {
  if (!randomState || randomState.isOver) return;
  randomSichern();
  randomNeuesZiel();
  updateRandomUI();
}

function undoRandomTurn() {
  if (randomHistoryStack.length === 0) return;
  const last = randomHistoryStack.pop();
  randomState = last.state;
  // Die Kacheln gehoeren zum zurueckgenommenen Zug - stehen zu bleiben
  // hiesse, sie waeren im wiederhergestellten Rest schon enthalten.
  randomEingabe.leere();

  if (randomHistoryStack.length === 0) {
    const undoBtn = document.getElementById("btn-undo-random");
    if (undoBtn) undoBtn.style.display = "none";
  }
  updateRandomUI();
}

// ------------------------------------------------------------------
// UI
// ------------------------------------------------------------------

function randomSetText(id, value) {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

function updateRandomUI() {
  if (!randomState) return;

  randomSetText("random-target-display", randomState.target ?? "-");
  randomSetText(
    "random-round-display",
    tf("js.random.006", { p1: randomState.currentRound, p2: randomConfig.rounds }),
  );
  randomSetText("random-hits-display", randomState.hits);
  randomSetText(
    "random-quote-display",
    randomState.attempts.length > 0
      ? `${Math.round((randomState.hits / randomState.attempts.length) * 100)} %`
      : "-",
  );
  randomSetText("random-player-label", randomState.playerName);

  // Checkout-Weg zum RESTBETRAG, nicht mehr zur Startzahl: Nach zwei Darts
  // ist der Weg zur urspruenglichen Zahl keine Hilfe mehr, sondern falsch.
  const path =
    typeof getCheckoutPath === "function" && randomState.rest
      ? getCheckoutPath(randomState.rest)
      : "";
  randomSetText("random-checkout-display", path ? tf("js.random.009", { p1: path }) : "");

  // Rest und Dartbudget des laufenden Versuchs. Solange noch kein Dart
  // geworfen ist, waere "Rest 121" bei Ziel 121 nur Wiederholung - dann
  // steht dort nur das Budget.
  const budget = tf("aufnahme.dartsUebrig", {
    p1: randomState.dartsUsedAttempt,
    p2: randomConfig.dartsPerAttempt,
  });
  randomSetText(
    "random-rest-display",
    randomState.dartsUsedAttempt > 0
      ? `${t("aufnahme.rest", "Rest")} ${randomState.rest} · ${budget}`
      : budget,
  );

  randomEingabe.aktualisiereZiel();
  randomEingabe.zeichne();
  updateRandomHistoryUI();
  // Nach jeder Standaenderung spiegeln (spielwache.js, 29.08.2026).
  if (typeof spielwacheTick === "function") spielwacheTick();
}

// ---------------------------------------------------------------------------
// EINGABE (14.08.2026): der Versuch wird geworfen, nicht gemeldet
//
// Vorher trug der Spieler hinterher ein, mit wie vielen Darts er gecheckt hat.
// Der Tracker kann das nicht - er liefert einzelne Darts. Also rechnet Random
// jetzt selbst mit: Jeder Dart zieht vom Rest ab, ein Doppel auf genau 0 ist
// geschafft, alles unter 2 ist ueberworfen.
//
// Die Aufnahme umfasst wie ueberall drei Darts. Ein Versuch darf laut
// randomConfig.dartsPerAttempt aber laenger sein (6 oder 9) - dann folgt auf
// die erste Aufnahme einfach die naechste, mit dem stehengebliebenen Rest.
// ---------------------------------------------------------------------------
const randomEingabe = erzeugeAufnahmeEingabe({
  modus: "random",
  ziel: () => document.getElementById("aufnahme-random"),
  aktiv: () =>
    document.getElementById("game-random-screen")?.style.display !== "none" &&
    !!randomState &&
    !randomState.isOver,
  // Beim Finish das Doppel hervorheben, auf das es jetzt ankommt - dieselbe
  // Regel wie beim OBS-Checkout-Zoom (Rest <= 40 und gerade, oder 50).
  zielFelder: () => {
    if (!randomState) return [];
    const rest = randomState.rest;
    if (rest === 50) return [{ base: 50 }];
    if (rest <= 40 && rest >= 2 && rest % 2 === 0) {
      return [{ base: rest / 2, mult: 2 }];
    }
    return [];
  },
  bilanz: (darts) => {
    const offen = randomState
      ? randomState.rest - darts.reduce((a, d) => a + Number(d.points || 0), 0)
      : 0;
    return { name: t("aufnahme.rest", "Rest"), wert: offen };
  },
  onAufnahme: (wuerfe) => {
    if (!randomState || randomState.isOver) return false;

    // Sichern, BEVOR irgendetwas sinkt: Der Rest aendert sich jetzt schon
    // waehrend der Aufnahme, nicht erst beim Abschluss des Versuchs.
    randomSichern();

    let rest = randomState.rest;
    let benutzt = randomState.dartsUsedAttempt;
    let ausgang = null; // "geschafft" | "verfehlt" | null (Versuch laeuft)

    for (const dart of wuerfe) {
      benutzt++;
      const neu = rest - Number(dart.points || 0);
      // Double-Out ist bei Random NICHT abschaltbar: RANDOM_BOGEY (159,
      // 162, 163, 165, 166, 168, 169) ist genau nach dieser Regel
      // zusammengestellt. Ohne sie waeren diese Zahlen checkbar und
      // muessten wieder in den Pool.
      if (neu === 0 && istCheckoutDart(dart)) {
        rest = 0;
        ausgang = "geschafft";
        break;
      }
      // Ueberworfen: unter null, genau eins (mit Doppel nicht checkbar) oder
      // null ohne Doppel. Der Versuch ist damit vorbei, auch wenn noch Darts
      // im Budget waeren - aus einem Bust laesst sich nicht weiterspielen.
      if (neu < 2) {
        ausgang = "verfehlt";
        break;
      }
      rest = neu;
      if (benutzt >= randomConfig.dartsPerAttempt) {
        ausgang = "verfehlt";
        break;
      }
    }

    randomState.rest = rest;
    randomState.dartsUsedAttempt = benutzt;

    if (ausgang === "geschafft") {
      showToast(tf("aufnahme.gecheckt", { p1: benutzt }), "success");
      randomWerteVersuch(benutzt);
    } else if (ausgang === "verfehlt") {
      randomWerteVersuch(0);
    } else {
      updateRandomUI(); // naechste Aufnahme, Rest bleibt stehen
    }
    return true;
  },
});

function updateRandomHistoryUI() {
  const list = document.getElementById("random-history-list");
  if (!list) return;
  list.innerHTML = "";

  [...randomState.attempts].reverse().forEach((a) => {
    const row = document.createElement("div");
    row.style.cssText =
      "display:flex; justify-content:space-between; padding:10px 14px; background:var(--glass-bg); border:1px solid var(--glass-border); margin-bottom:6px; border-radius:8px; font-size:0.85em;";

    const left = document.createElement("span");
    left.style.color = "var(--text-muted)";
    left.textContent = `R${a.round} · ${a.target}`;

    const right = document.createElement("span");
    right.style.fontWeight = "800";
    right.style.color = a.success ? "var(--accent-green)" : "var(--accent-red)";
    right.textContent = a.success ? tf("js.friends.011", { p1: a.darts }) : "verfehlt";

    row.appendChild(left);
    row.appendChild(right);
    list.appendChild(row);
  });

  list.scrollTop = 0;
}

// ------------------------------------------------------------------
// ABSCHLUSS & STATS
// ------------------------------------------------------------------

function finishRandomGame() {
  const hits = randomState.hits;
  const total = randomConfig.rounds;
  saveRandomStatsBackground();
  spielwacheSpielBeendet();

  // Gemeinsamer Endbildschirm statt Toast + goHome (Produkttest
  // 29.08.2026).
  zeigeEndbildschirm({
    screenId: "game-random-screen",
    titel: t("js.ende.fertig"),
    untertitel: tf("js.random.007", { p1: hits, p2: total }),
    zeilen: [],
    kennzahlen: [
      { label: t("aufnahme.treffer"), wert: `${hits}/${total}` },
    ],
    revanche: () => startRandomGame(),
    setup: () => openRandomSetup(),
  });
}

async function saveRandomStatsBackground() {
  if (!randomState) return;
  try {
    const attempts = randomState.attempts.length;
    const payload = {
      name: randomState.playerName,
      hits: randomState.hits,
      attempts: attempts,
      quote: attempts > 0 ? Number((randomState.hits / attempts).toFixed(4)) : 0,
      darts_used: randomState.dartsUsedTotal,
      min_target: randomConfig.minTarget,
      max_target: randomConfig.maxTarget,
      darts_per_attempt: randomConfig.dartsPerAttempt,
      rounds: randomConfig.rounds,
      details: randomState.attempts,
    };

    if (typeof currentUser !== "undefined" && currentUser) {
      payload.user_id = currentUser.id;
    }

    const { error } = await _supabase.from("stats_random").insert([payload]);
    if (error) {
      console.error(t("js.random.004"), error.message);
    }
  } catch (err) {
    console.error(t("js.g121.002"), err);
  }
}

// ---------------------------------------------------------------
// Bruecke zu window: Inline-Handler im HTML und andere Module greifen
// weiterhin global auf diese Funktionen zu. Automatisch erzeugt beim
// Umstieg auf Vite - Modul-Scope ersetzt sonst die alten Globals.
// ---------------------------------------------------------------
Object.assign(window, {
  openRandomSetup,
  applyRandomPreset,
  startRandomGame,
  cancelRandomGame,
  resetRandomState,
  skipRandomTarget,
  undoRandomTurn,
  submitRandomAttempt,
  randomEingabe,
});
